// CW staff-notes chat + Audit trail tab (field changes, saves, status changes,
// Quote Notes Summary snapshots). Snapshots live in StaffNotes but only show here.
import { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, Activity, History, ChevronRight, ChevronDown, FileText, PencilLine, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import VoiceNoteButton from "./VoiceNoteButton";
import { BuildTypeHelp } from "./BuildTypeControls";
import MentionTextarea, { MentionText, mentionsIn, useStaff } from "./MentionTextarea";
import { timeAgo } from "../_quoteHelpers";
import { T2Panel } from "../_t2";
import { InitialBadge } from "../_AssigneeStack";

export const SNAP_PREFIX = "QUOTE SUMMARY SNAPSHOT";
const isSnap = (n) => String(n?.body || "").trim().startsWith(SNAP_PREFIX);
const fmtTs = (ts) => { const d = new Date(ts); return isNaN(d) ? "" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); };

function TabBtn({ on, onClick, icon: Icon, label, count, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} data-on={on ? "1" : "0"} className="snc-tab"
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6, cursor: "pointer", fontSize: 10.5, fontWeight: 700,
        fontFamily: "var(--t2-mono)", border: `1px solid ${on ? "var(--t2-orange)" : "var(--t2-border)"}`,
        background: on ? "rgba(255,104,32,0.14)" : "transparent", color: on ? "var(--t2-orange-ink)" : "var(--t2-grey2)", transition: "background-color .15s, border-color .15s" }}>
      <Icon className="w-3 h-3" /> {label} <span style={{ opacity: .75 }}>{count}</span>
    </button>
  );
}

function AuditRow({ e, i }) {
  const [open, setOpen] = useState(false);
  const snap = e.type === "snapshot";
  const Icon = snap ? FileText : PencilLine;
  const head = snap ? (e.body.split("\n").find((l) => l.startsWith("Status change:")) || "").replace("Status change:", "Status change ·").trim() || "Quote summary snapshot" : e.text;
  return (
    <div data-testid={`audit-row-${i}`} style={{ display: "flex", gap: 9, padding: "7px 2px", borderBottom: "1px solid var(--t2-border)" }}>
      <Icon className="w-3.5 h-3.5" style={{ marginTop: 2, flexShrink: 0, color: snap ? "var(--t2-teal-ink)" : "var(--t2-grey3)" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {snap ? (
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`audit-snapshot-toggle-${i}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--t2-teal-ink)", fontSize: 11.5, fontWeight: 700, textAlign: "left" }}>
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {head}
          </button>
        ) : <div style={{ fontSize: 11.5, color: "var(--t2-grey1)", lineHeight: 1.45, wordBreak: "break-word" }}>{head}</div>}
        {snap && open && <pre className="t2-mono" data-testid={`audit-snapshot-body-${i}`} style={{ margin: "6px 0 0", fontSize: 10.5, lineHeight: 1.5, whiteSpace: "pre-wrap", color: "var(--t2-grey1)", background: "var(--t2-ink)", border: "1px solid var(--t2-border)", borderRadius: 8, padding: "8px 10px" }}>{e.body}</pre>}
        <div className="t2-note-meta">{e.by || "system"} · {fmtTs(e.ts)} · {timeAgo(e.ts)}</div>
      </div>
    </div>
  );
}

export default function StaffNotesChat({ quoteId, buildType = "", notes, audit = [], onSend, currentUserName, onLog, onDraftActivity, canEditAny, onEdited }) {
  const [text, setText] = useState("");
  const [tab, setTab] = useState("notes");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const { user } = useAuth() || {};
  const canEdit = (n) => !n.system && (canEditAny || (n.by_id ? n.by_id === String(user?.id || user?._id || "") : (n.by || "").trim().toLowerCase() === String(currentUserName || "").trim().toLowerCase()));
  const saveEdit = async (n) => {
    const body = editText.trim();
    if (!body || body === n.body) { setEditId(null); return; }
    try {
      await api.patch(`/quotes/${quoteId}/notes/${n.id}`, { body });
      toast.success("Note updated");
      setEditId(null);
      await onEdited?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update note"); }
  };
  const endRef = useRef(null);
  const human = useMemo(() => notes.filter((n) => !isSnap(n)), [notes]);
  const trail = useMemo(() => [
    ...notes.filter(isSnap).map((n) => ({ type: "snapshot", ts: n.ts, by: n.by, body: n.body, key: n.id })),
    ...audit.filter((a) => a.kind !== "NOTE").map((a) => ({ type: "change", ts: a.ts, by: a.by, text: a.description, key: a.id })),
  ].sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || ""))), [notes, audit]);
  const prevLenRef = useRef(human.length);
  // Only auto-scroll when a note is *added* — never on initial mount.
  useEffect(() => {
    if (human.length > prevLenRef.current) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    prevLenRef.current = human.length;
  }, [human.length]);

  const staff = useStaff();
  const submit = async () => {
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    await onSend(body, mentionsIn(body, staff));
  };

  return (
    <T2Panel
      icon={MessageSquare}
      title={<span style={{ color: "var(--t2-brass, #C17A4A)" }}>CW Staff Notes</span>}
      testid="panel-staff-notes"
      right={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <TabBtn on={tab === "notes"} onClick={() => setTab("notes")} icon={MessageSquare} label="Notes" count={human.length} testid="staff-notes-tab-notes" />
          <TabBtn on={tab === "audit"} onClick={() => setTab("audit")} icon={History} label="Audit trail" count={trail.length} testid="staff-notes-tab-audit" />
          {onLog && (
            <button type="button" onClick={() => onLog("activity")} data-testid="staff-notes-log-activity" title="Log an activity against this customer"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "var(--t2-mono)",
                background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}>
              <Activity className="w-3 h-3" /> Log activity
            </button>
          )}
          <span style={{ display: "inline-flex", gap: 4 }} data-testid="staff-notes-build-help"><BuildTypeHelp value={buildType} /></span>
        </span>
      }
    >
      {tab === "audit" ? (
        <div className="t2-scroll" style={{ maxHeight: 300, overflowY: "auto", paddingRight: 4 }} data-testid="staff-notes-audit-list">
          {trail.length === 0 && <div style={{ fontSize: 11, color: "var(--t2-grey3)", textAlign: "center", padding: "12px 0" }}>No changes logged yet.</div>}
          {trail.map((e, i) => <AuditRow key={e.key || i} e={e} i={i} />)}
        </div>
      ) : (
        <>
          <div className="t2-scroll" style={{ maxHeight: 240, overflowY: "auto", paddingRight: 4 }} data-testid="staff-notes-list">
            {human.length === 0 && <div style={{ fontSize: 11, color: "var(--t2-grey3)", textAlign: "center", padding: "12px 0" }}>No notes yet.</div>}
            {human.map((n) => n.system ? (
              <div key={n.id} className="t2-note-row" data-testid={`staff-note-system-${n.id}`}>
                <span style={{ width: 26, height: 26, borderRadius: 999, display: "grid", placeItems: "center", background: "rgba(139,92,246,.18)", border: "1px solid rgba(139,92,246,.45)", flexShrink: 0 }}>
                  <Star className="w-3.5 h-3.5" style={{ color: "#c4b5fd", fill: "#c4b5fd" }} />
                </span>
                <div className="t2-note-bubble" style={{ borderColor: "rgba(139,92,246,.4)", background: "rgba(139,92,246,.08)" }}>
                  <div className="t2-note-text" style={{ color: "#ddd6fe" }}>{String(n.body).replace(/^★\s*/, "")}</div>
                  <div className="t2-note-meta">System · {fmtTs(n.ts)} · {timeAgo(n.ts)}</div>
                </div>
              </div>
            ) : (
              <div key={n.id} className="t2-note-row" data-testid={`staff-note-${n.id}`}>
                <InitialBadge name={n.by || "?"} size={26} />
                <div className="t2-note-bubble" style={{ flex: editId === n.id ? 1 : undefined }}>
                  {editId === n.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <textarea className="t2-input" rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`staff-note-edit-input-${n.id}`} autoFocus />
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button type="button" className="t2-btn-ghost" style={{ padding: "3px 10px", fontSize: 10.5 }} onClick={() => setEditId(null)} data-testid={`staff-note-edit-cancel-${n.id}`}>Cancel</button>
                        <button type="button" className="t2-btn-primary" style={{ padding: "3px 10px", fontSize: 10.5 }} onClick={() => saveEdit(n)} data-testid={`staff-note-edit-save-${n.id}`}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="t2-note-text"><MentionText body={n.body} mentions={n.mentions} /></div>
                  )}
                  <div className="t2-note-meta" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{n.by} · {timeAgo(n.ts)}{n.edited_at ? ` · edited${n.edited_by && n.edited_by !== n.by ? ` by ${n.edited_by}` : ""}` : ""}</span>
                    {quoteId && editId !== n.id && canEdit(n) && (
                      <button type="button" onClick={() => { setEditId(n.id); setEditText(n.body); }} title="Edit this note" data-testid={`staff-note-edit-${n.id}`}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--t2-grey3)", padding: 0, display: "inline-flex" }}>
                        <PencilLine className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--t2-border)" }}>
            <VoiceNoteButton quoteId={quoteId} onText={(t) => setText((cur) => (cur.trim() ? `${cur.trim()} ${t}` : t))} />
            <MentionTextarea value={text} onChange={setText} onSubmit={submit} testid="staff-note-input"
              placeholder={`Type or record a note as ${currentUserName || "you"}… (@ to mention)`} />
            <button onClick={submit} className="t2-btn-primary" style={{ padding: "0 12px" }} data-testid="staff-note-send">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
          {onDraftActivity && human.length > 0 && (
            <button type="button" onClick={onDraftActivity} data-testid="staff-notes-ai-activity" className="t2-mono"
              style={{ marginTop: 6, background: "none", border: "none", cursor: "pointer", color: "#c4b5fd", fontSize: 10.5, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Sparkles className="w-3 h-3" /> Turn these notes into an activity (AI draft — you approve)
            </button>
          )}
        </>
      )}
    </T2Panel>
  );
}
